import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { Field } from '../auth/ui/Field'
import { currentLocation } from '../../app/location'
import { useMapsOn } from '../../app/settings'
import { useAddressSuggest } from './api'
import { AddressMap } from './AddressMap'
import type { AddressDraft } from './addressDraft'
import { useDebounced } from '../../app/useDebounced'

export interface AddressFinderProps {
  draft: AddressDraft
  /** Что написать, пока адрес не выбран. */
  hint?: string
}

/**
 * Поиск с подсказками геокодера, карта, на которой точку ставят нажатием,
 * кнопка «Где я» и выбранный адрес строкой. Общие для адреса оформления и
 * «Моих адресов» в кабинете.
 */
export function AddressFinder({ draft, hint }: AddressFinderProps) {
  const { t, i18n } = useTranslation(['checkout', 'common'])
  const { picked, spot, setSpot, query, setQuery, reverse, selected, take } = draft
  const [locating, setLocating] = useState(false)
  const [locateFailed, setLocateFailed] = useState(false)
  const suggest = useAddressSuggest(useDebounced(query, 300), i18n.language)
  const mapsOn = useMapsOn()

  // Пока не ясно, включены ли карты, — ничего: иначе мелькнула бы карта.
  if (mapsOn === null) return null
  // Карты выключены в админке: без карты и геокодера у нового адреса нет
  // точки, а без неё сервер не найдёт аптеку. Уже выбранный адрес — остаётся.
  if (!mapsOn) {
    return (
      <>
        <div className="card empty" role="status">
          <Icon name="i-pin" size={54} />
          <div className="t">{t('checkout:addressPage.mapsOff')}</div>
          {t('checkout:addressPage.mapsOffHint')}
        </div>
        {selected ? (
          <div className="card addr-chosen">
            <Icon name="i-pin" size={18} />
            <div className="t1">{selected.label}</div>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <>
      <div className="card addr-find">
        <div className="addr-search">
          <Field label={t('checkout:addressPage.searchLabel')}>
            {({ id }) => (
              <input
                id={id}
                type="search"
                autoComplete="street-address"
                placeholder={t('checkout:addressPage.search')}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                }}
              />
            )}
          </Field>
        </div>
        {suggest.data?.map((address) => (
          <Press
            key={`${address.label}:${address.lat}:${address.lng}`}
            squish="row"
            className="opt addr-sug"
            onPress={() => {
              take(address)
            }}
          >
            <Icon name="i-pin" size={17} />
            <div className="t1">{address.label}</div>
          </Press>
        ))}
        {suggest.data?.length === 0 ? (
          <div className="list-empty">{t('checkout:addressPage.nothing')}</div>
        ) : null}
        {suggest.isError ? (
          <div className="list-empty">{t('checkout:addressPage.failed')}</div>
        ) : null}
      </div>

      <div className="addr-map-box">
        <AddressMap point={spot ?? picked} onPick={setSpot} />
        <Press
          squish="icon"
          className="addr-locate"
          aria-label={t('checkout:addressPage.locate')}
          disabled={locating}
          aria-busy={locating || undefined}
          onPress={() => {
            setLocating(true)
            setLocateFailed(false)
            void currentLocation().then((point) => {
              setLocating(false)
              if (point) setSpot(point)
              else setLocateFailed(true)
            })
          }}
        >
          <Icon name="i-locate" size={20} />
        </Press>
      </div>
      {locateFailed ? (
        <p className="acc-note acc-note-err" role="status">
          {t('checkout:addressPage.locateFailed')}
        </p>
      ) : null}

      <div className="card addr-chosen" aria-live="polite">
        <Icon name="i-pin" size={18} />
        <div className="t1">
          {selected?.label ??
            (spot
              ? reverse.isError
                ? t('checkout:addressPage.reverseFailed')
                : t('checkout:addressPage.locating')
              : (hint ?? t('checkout:addressPage.hint')))}
        </div>
      </div>
    </>
  )
}
